import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";

const LOGIN_BG_VIDEO =
  "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/sustainable_navratri/main.mp4";
const LOGIN_BG_VIDEO_MOBILE =
  "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/sustainable_navratri/mob_nav.mp4";

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);
  const isMobilePortrait = useMobilePortrait();
  const mobileVideoRef = useRef(null);
  const desktopVideoRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    // iOS/WebKit bug: a `position: fixed` element (.login-hero) can keep a
    // stale, often much-too-small layout height instead of recomputing
    // against the current viewport — leaving most of the screen blank/white
    // below whatever sliver did get sized. This isn't only triggered by
    // rotation: it reliably shows up right after the OTP keyboard dismisses
    // on login success, because the keyboard hide is itself a viewport
    // resize that WebKit fails to re-layout fixed elements for. Forcing a
    // reflow (toggle display off/on) works around it, but it has to run
    // once the stale height is actually baked in, not just on every future
    // resize/orientationchange — otherwise a device that never rotates
    // stays stuck on the too-small height forever.
    const forceReflow = () => {
      const el = heroRef.current;
      if (!el) return;
      el.style.display = "none";
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;
      el.style.display = "";
    };

    // Run once after mount/paint to correct any stale height already baked
    // in from before this component existed (e.g. the OTP keyboard closing
    // on the previous screen, just before navigating here). A second,
    // delayed run covers the case where iOS hasn't finished settling its
    // internal viewport metrics by the first animation frame — the keyboard
    // dismiss animation itself takes a couple hundred ms.
    const raf = requestAnimationFrame(forceReflow);
    const timeout = setTimeout(forceReflow, 350);

    window.addEventListener("orientationchange", forceReflow);
    window.addEventListener("resize", forceReflow);
    // iOS fires viewport-only resizes (e.g. keyboard show/hide) through
    // visualViewport, not always through window's resize event.
    window.visualViewport?.addEventListener("resize", forceReflow);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("orientationchange", forceReflow);
      window.removeEventListener("resize", forceReflow);
      window.visualViewport?.removeEventListener("resize", forceReflow);
    };
  }, []);

  useEffect(() => {
    // iOS Safari/WKWebView can silently refuse autoplay when `muted` isn't
    // set as a real DOM property before the play request (React setting it
    // as a JSX prop is sometimes too late), leaving the video blank/white.
    //
    // Both the mobile-portrait and desktop video elements are mounted once
    // and kept alive for the component's whole lifetime (see JSX below) —
    // rotation only toggles which one is visible via CSS. Previously the
    // active <video> was swapped by changing `key`, which unmounted and
    // recreated the DOM node on every portrait/landscape crossing; the new
    // node's readyState reset to 0 (no decoded frame) until it re-buffered,
    // and since nothing behind it has an opaque background, that reload gap
    // showed up as a white patch — reliably in portrait, since every
    // portrait entry re-triggered a fresh fetch/decode of the mobile video.
    // Keeping both elements permanently mounted and pre-buffered removes
    // that reload entirely; swapping is just an opacity flip between two
    // already-playing videos.
    const setupAutoplay = (el) => {
      if (!el) return () => {};

      const tryPlay = () => {
        el.muted = true;
        const playPromise = el.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      };

      el.defaultMuted = true;
      el.load();
      tryPlay();

      // On iOS, play() called immediately on mount often fires before the
      // element has buffered enough data, and the resulting rejection is
      // swallowed above — it's never retried, which is why the login-success
      // path (no forced dialog/tap in front of it) can end up with a blank
      // white video while the login-failure path (user taps "OK" on the
      // SweetAlert) happens to work. Retry once the media itself reports
      // it's ready, independent of any user gesture.
      el.addEventListener("loadedmetadata", tryPlay);
      el.addEventListener("loadeddata", tryPlay);
      el.addEventListener("canplay", tryPlay);
      el.addEventListener("canplaythrough", tryPlay);

      // A JS-synthesized click doesn't count as a user gesture on iOS, so
      // autoplay can still be blocked after the above. As a fallback, retry
      // play() inside the very first *real* tap/click anywhere on the page —
      // that does carry the user-activation iOS requires.
      const controller = new AbortController();
      const opts = { once: true, passive: true, signal: controller.signal };
      document.addEventListener("touchstart", tryPlay, opts);
      document.addEventListener("pointerdown", tryPlay, opts);
      document.addEventListener("click", tryPlay, opts);

      return () => {
        controller.abort();
        el.removeEventListener("loadedmetadata", tryPlay);
        el.removeEventListener("loadeddata", tryPlay);
        el.removeEventListener("canplay", tryPlay);
        el.removeEventListener("canplaythrough", tryPlay);
      };
    };

    const cleanupMobile = setupAutoplay(mobileVideoRef.current);
    const cleanupDesktop = setupAutoplay(desktopVideoRef.current);
    return () => {
      cleanupMobile();
      cleanupDesktop();
    };
  }, []);

  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;
      setBeginLoading(true);
      try {
        navigate("/intro", { replace: true });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, navigate]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    // Login screen must be theme-independent: no themed colors and no background images.
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    document.body.classList.remove("common-bg");
    document.body.style.backgroundImage = "none";
    document.body.style.background = "none";
    document.body.style.backgroundAttachment = "initial";
    document.body.style.backgroundPosition = "initial";
    document.body.style.backgroundSize = "initial";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

// show "already played" when login payload has gameover > 0 (DB stages.gameover)
useEffect(() => {
  if (isAdmin) return;
  if (status !== "succeeded" || !user) return;
  if (Number(user.gameover ?? 0) > 0) {
    const msg = error ? String(error) : "You have already played!";
    const redirect = user.backButtonRedirect || "/";
    Swal.fire("You have already played!", "  ", "error").then(() => {
      window.location.href = redirect;
    });
  }
}, [status, user, error, isAdmin]);

  return (
    <div className="login-hero" ref={heroRef}>
      <video
        ref={mobileVideoRef}
        className={`login-video${isMobilePortrait ? " is-active" : ""}`}
        src={LOGIN_BG_VIDEO_MOBILE}
        autoPlay
        loop
        muted
        defaultMuted
        playsInline
        webkit-playsinline="true"
        preload="auto"
        disablePictureInPicture
      />
      <video
        ref={desktopVideoRef}
        className={`login-video${!isMobilePortrait ? " is-active" : ""}`}
        src={LOGIN_BG_VIDEO}
        autoPlay
        loop
        muted
        defaultMuted
        playsInline
        webkit-playsinline="true"
        preload="auto"
        disablePictureInPicture
      />

      <div className="login-overlay">
        {!isAdmin ? (
          <button
            type="button"
            className="begin-play-btn"
            disabled={beginLoading || status !== "succeeded" || !user}
            onClick={handleBeginPlay}
          >
            {beginLoading ? "LOADING…" : "BEGIN PLAY"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default Login;
